class LMHeader extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.events = new AbortController();
    const listen = (target, type, fn) => target.addEventListener(type, fn, { signal: this.events.signal });
    this.dialog = this.querySelector('dialog');
    const menuToggle = this.querySelector('[data-lm-menu-toggle]');
    const menu = this.querySelector('[data-lm-mobile-menu]');
    listen(menuToggle, 'click', () => {
      menu.hidden = !menu.hidden; menu.classList.toggle('is-open', !menu.hidden);
      menuToggle.setAttribute('aria-expanded', String(!menu.hidden));
      menuToggle.setAttribute('aria-label', menu.hidden ? 'Open menu' : 'Close menu');
    });
    listen(this, 'keydown', e => { if (e.key === 'Escape' && !menu.hidden) { menuToggle.click(); menuToggle.focus(); } });
    const input = this.querySelector('[data-lm-search-input]');
    const opener = this.querySelector('[data-lm-search-open]');
    listen(opener, 'click', e => { e.preventDefault(); this.dialog.showModal(); document.documentElement.classList.add('lm-modal-open'); input.focus(); });
    listen(this.querySelector('[data-lm-search-close]'), 'click', () => this.dialog.close());
    listen(this.dialog, 'click', e => { if (e.target === this.dialog) this.dialog.close(); });
    listen(this.dialog, 'keydown', e => { if (e.key === 'Escape') { e.preventDefault(); this.dialog.close(); } });
    listen(this.dialog, 'close', () => { document.documentElement.classList.remove('lm-modal-open'); opener.focus(); });
    listen(input, 'input', () => {
      clearTimeout(this.timer); this.searchController?.abort();
      const query = input.value.trim();
      if (!query) { this.querySelector('[data-lm-search-results]').replaceChildren(); this.querySelector('[data-lm-search-status]').textContent = 'Start typing to search.'; return; }
      this.timer = setTimeout(() => this.search(query), 200);
    });
    listen(input, 'keydown', e => { if (e.key === 'ArrowDown') { const link = this.querySelector('[data-lm-search-results] a'); if (link) { e.preventDefault(); link.focus(); } } });
    listen(this.querySelector('[data-lm-search-results]'), 'keydown', e => {
      if (!['ArrowDown','ArrowUp'].includes(e.key)) return;
      const links = [...this.querySelectorAll('[data-lm-search-results] a')];
      const next = links.indexOf(document.activeElement) + (e.key === 'ArrowDown' ? 1 : -1);
      e.preventDefault(); (next < 0 ? input : links[Math.min(next, links.length - 1)])?.focus();
    });
    const pause = this.querySelector('[data-lm-ticker-pause]');
    if (pause) listen(pause, 'click', () => {
      const paused = pause.getAttribute('aria-pressed') !== 'true';
      pause.setAttribute('aria-pressed', String(paused)); pause.setAttribute('aria-label', paused ? 'Resume announcements' : 'Pause announcements');
      this.querySelector('.lm-ticker').classList.toggle('is-paused', paused); pause.textContent = paused ? '▶' : 'Ⅱ';
    });
    const resize = () => document.documentElement.style.setProperty('--lm-header-height', this.getBoundingClientRect().height + 'px');
    this.observer = new ResizeObserver(resize); this.observer.observe(this); resize();
    const sync = () => this.syncCart();
    ['cart:updated','cart:refresh','cart:change','cart:change:success','ajaxProduct:added','cart:update'].forEach(type => listen(document, type, sync));
    listen(window, 'pageshow', sync); listen(window, 'focus', sync);
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) this.unsubscribe = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, sync);
    this.syncCart();
  }
  async search(query) {
    this.searchController?.abort(); this.searchController = new AbortController();
    const token = this.searchController;
    const status = this.querySelector('[data-lm-search-status]');
    const results = this.querySelector('[data-lm-search-results]');
    status.textContent = 'Searching…'; results.setAttribute('aria-busy', 'true');
    results.replaceChildren();
    try {
      const url = new URL(window.routes.predictive_search_url, location.origin);
      url.searchParams.set('q', query); url.searchParams.set('resources[type]', 'product,article,page,query');
      url.searchParams.set('resources[limit]', '6'); url.searchParams.set('section_id', 'lm-predictive-search');
      const response = await fetch(url, { signal: token.signal });
      if (!response.ok) throw new Error('Search unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-lm-predictive-content]');
      if (!content) throw new Error('Missing search results');
      if (this.querySelector('[data-lm-search-input]').value.trim() !== query) return;
      results.replaceChildren(content); status.textContent = 'Search results updated.';
    } catch (e) { if (e.name !== 'AbortError') status.textContent = 'Live search is temporarily unavailable. Press Search to view results.'; }
    finally { if (this.searchController === token) results.removeAttribute('aria-busy'); }
  }
  async syncCart() {
    try {
      const response = await fetch(this.dataset.cartUrl, { cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();
      this.querySelector('[data-lm-cart-count]').textContent = cart.item_count;
      const shipping = this.querySelector('[data-lm-shipping]'); if (!shipping) return;
      const goal = Number(shipping.dataset.goal); if (goal <= 0) return;
      const left = Math.max(0, goal - cart.total_price), percent = Math.min(100, cart.total_price / goal * 100);
      const money = window.LM.formatMoney(left, this.dataset.moneyFormat);
      shipping.querySelector('[data-lm-shipping-fill]').style.width = percent + '%';
      shipping.querySelector('[role="progressbar"]').setAttribute('aria-valuenow', Math.round(percent));
      shipping.querySelector('[data-lm-shipping-value]').textContent = left ? money + ' left' : 'Free shipping';
      shipping.querySelector('[data-lm-shipping-copy]').textContent = left ? 'Add ' + money + ' more to unlock free shipping.' : 'Free shipping unlocked.';
    } catch (_) { /* Server-rendered totals remain accurate until another update succeeds. */ }
  }
  disconnectedCallback() { this.events?.abort(); this.observer?.disconnect(); this.unsubscribe?.(); this.searchController?.abort(); clearTimeout(this.timer); this.ready = false; }
}
if (!customElements.get('lm-header')) customElements.define('lm-header', LMHeader);
